import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { MapContainer, TileLayer, useMap, ZoomControl } from 'react-leaflet';
import L from 'leaflet';
import MapErrorBoundary from './MapErrorBoundary';
import MapPropertyPreviewCard from './MapPropertyPreviewCard';
import MapFirstToolbar from './MapFirstToolbar';
import HotelMapPinCluster from './HotelMapPinCluster';
import MapSearchAreaControl from './MapSearchAreaControl';
import { hasGoogleMapsKey, LEAFLET_GOOGLE_STYLE_TILE } from '../config/mapConfig';
import { bucketPins, cityOverviewZoom } from '../utils/mapPinCluster';
import { googleBoundsToArea } from '../utils/mapSearchArea';
import { useHotelMapPins, DEFAULT_MAP_CENTER } from '../hooks/useHotelMapPins';
import { loadGoogleMaps } from '../utils/googleMapsLoader';
import { GOOGLE_MAPS_API_KEY } from '../config/mapConfig';
import 'leaflet/dist/leaflet.css';
import './SearchResultsMap.css';
import './MapFirstToolbar.css';

export { getHotelCoordinates } from '../utils/mapCoordinates';

delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png',
  iconUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png',
  shadowUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png',
});

const MAP_TILE = LEAFLET_GOOGLE_STYLE_TILE;
const STAYS_MAP_USP = 'Tap a price pin to preview and book your stay.';

const SearchThisAreaButton = ({ visible, loading, onClick }) => {
  if (!visible) return null;
  return (
    <button
      type="button"
      className="search-this-area-btn"
      onClick={onClick}
      disabled={loading}
      aria-label="Search this map area"
    >
      {loading ? 'Searching area…' : 'Search this area'}
    </button>
  );
};

const MapResize = ({ trigger }) => {
  const map = useMap();
  useEffect(() => {
    const invalidate = () => map.invalidateSize({ animate: false, pan: false });
    invalidate();
    const raf = window.requestAnimationFrame(invalidate);
    const t1 = window.setTimeout(invalidate, 80);
    const t2 = window.setTimeout(invalidate, 350);
    const wrap = map.getContainer()?.closest('.search-results-map-wrap');
    let observer;
    if (wrap && typeof ResizeObserver !== 'undefined') {
      observer = new ResizeObserver(invalidate);
      observer.observe(wrap);
    }
    window.addEventListener('resize', invalidate);
    return () => {
      window.cancelAnimationFrame(raf);
      window.clearTimeout(t1);
      window.clearTimeout(t2);
      observer?.disconnect();
      window.removeEventListener('resize', invalidate);
    };
  }, [map, trigger]);
  return null;
};

const FitBounds = ({ hotelPoints, fallbackCenter, totalPins = 0 }) => {
  const map = useMap();
  useEffect(() => {
    map.invalidateSize({ animate: false, pan: false });

    if (totalPins > 20 && fallbackCenter) {
      map.setView([fallbackCenter.lat, fallbackCenter.lng], cityOverviewZoom(totalPins));
      return;
    }

    if (hotelPoints.length > 1) {
      const bounds = L.latLngBounds(hotelPoints.map((p) => [p.lat, p.lng]));
      map.fitBounds(bounds, { padding: [48, 48], maxZoom: 13 });
      return;
    }
    if (hotelPoints.length === 1) {
      map.setView([hotelPoints[0].lat, hotelPoints[0].lng], 13);
      return;
    }
    if (fallbackCenter) map.setView([fallbackCenter.lat, fallbackCenter.lng], 11);
  }, [map, hotelPoints, fallbackCenter, totalPins]);
  return null;
};

const PanToSelected = ({ lat, lng, active }) => {
  const map = useMap();
  useEffect(() => {
    if (!active || lat == null || lng == null) return;
    map.panTo([lat, lng], { animate: true, duration: 0.35 });
  }, [map, lat, lng, active]);
  return null;
};

const createClusterPinOverlayClass = (maps) =>
  class ClusterPinOverlay extends maps.OverlayView {
    constructor({ position, count, onClick }) {
      super();
      this.position = position;
      this.count = count;
      this.onClick = onClick;
      this.div = null;
    }

    onAdd() {
      const div = document.createElement('div');
      div.className = 'srm-cluster-pin';
      div.innerHTML = `<span>${this.count}</span><small>stays</small>`;
      div.style.cursor = 'pointer';
      div.addEventListener('click', (event) => {
        event.stopPropagation();
        this.onClick?.();
      });
      this.div = div;
      this.getPanes().floatPane.appendChild(div);
    }

    draw() {
      if (!this.div || !this.getProjection()) return;
      const point = this.getProjection().fromLatLngToDivPixel(this.position);
      if (!point) return;
      this.div.style.position = 'absolute';
      this.div.style.left = `${point.x}px`;
      this.div.style.top = `${point.y}px`;
      this.div.style.transform = 'translate(-50%, -50%)';
      this.div.style.zIndex = '500';
    }

    onRemove() {
      if (this.div?.parentNode) this.div.parentNode.removeChild(this.div);
      this.div = null;
    }
  };

const createPricePinOverlayClass = (maps) =>
  class PricePinOverlay extends maps.OverlayView {
    constructor({ position, priceLabel, isActive, onClick }) {
      super();
      this.position = position;
      this.priceLabel = priceLabel;
      this.isActive = isActive;
      this.onClick = onClick;
      this.div = null;
    }

    onAdd() {
      const div = document.createElement('div');
      div.className = `srm-price-pin ${this.isActive ? 'srm-price-pin--active' : ''}`;
      div.textContent = this.priceLabel;
      div.style.cursor = 'pointer';
      div.addEventListener('click', (event) => {
        event.stopPropagation();
        this.onClick?.();
      });
      this.div = div;
      this.getPanes().floatPane.appendChild(div);
    }

    draw() {
      if (!this.div || !this.getProjection()) return;
      const point = this.getProjection().fromLatLngToDivPixel(this.position);
      if (!point) return;
      this.div.style.position = 'absolute';
      this.div.style.left = `${point.x}px`;
      this.div.style.top = `${point.y}px`;
      this.div.style.transform = 'translate(-50%, -100%)';
      this.div.style.zIndex = this.isActive ? '1000' : '1';
      this.div.className = `srm-price-pin ${this.isActive ? 'srm-price-pin--active' : ''}`;
    }

    onRemove() {
      if (this.div?.parentNode) this.div.parentNode.removeChild(this.div);
      this.div = null;
    }
  };

const mapToolbarProps = (viewMode, onViewModeChange, mapFullScreen, onToggleMapFullScreen) => ({
  showLandmarkFilters: false,
  uspText: STAYS_MAP_USP,
  viewMode,
  onViewModeChange,
  isFullScreen: mapFullScreen,
  onToggleFullScreen: onToggleMapFullScreen,
});

const SearchResultsLeafletMap = ({
  hotels,
  selectedHotelId,
  onSelectHotel,
  onViewDetails,
  onClearSelection,
  defaultCenter,
  searchQuery,
  checkIn,
  checkOut,
  viewMode,
  onViewModeChange,
  mapFullScreen,
  onToggleMapFullScreen,
  searchPlace,
  showSearchAreaBtn,
  mapAreaLoading,
  onMapMoved,
  onSearchThisArea,
  onPinsChange,
}) => {
  const [mapSearch, setMapSearch] = useState('');
  const { pins, mapCenter, geocoding } = useHotelMapPins({
    hotels,
    searchQuery,
    checkIn,
    checkOut,
    defaultCenter,
    searchCenter: searchPlace?.center || null,
    onPinsChange,
  });

  const filteredPins = useMemo(() => {
    const q = mapSearch.trim().toLowerCase();
    if (!q) return pins;
    return pins.filter((m) => {
      const hay = `${m.name} ${m.hotel.city || ''} ${m.hotel.address || ''}`.toLowerCase();
      return hay.includes(q);
    });
  }, [pins, mapSearch]);

  const hotelPoints = useMemo(() => filteredPins.map((m) => ({ lat: m.lat, lng: m.lng })), [filteredPins]);

  const previewPin = useMemo(() => {
    if (!selectedHotelId) return null;
    return pins.find((m) => String(m.id) === String(selectedHotelId)) || null;
  }, [pins, selectedHotelId]);

  const handlePinClick = (pin) => {
    if (selectedHotelId && String(selectedHotelId) === String(pin.id)) onClearSelection?.();
    else onSelectHotel?.(pin.hotel);
  };

  const center = searchPlace?.center || pins[0] || mapCenter || defaultCenter;

  return (
    <div className={`search-results-map-panel ${mapFullScreen ? 'search-results-map-panel--fullscreen' : ''}`}>
      <MapFirstToolbar {...mapToolbarProps(viewMode, onViewModeChange, mapFullScreen, onToggleMapFullScreen)} />
      <div className="search-results-map-search">
        <input
          type="text"
          value={mapSearch}
          onChange={(e) => setMapSearch(e.target.value)}
          placeholder="Search on map"
          aria-label="Search on map"
        />
      </div>
      <div className="search-results-map-wrap">
        <MapErrorBoundary>
          <MapContainer
            center={[center.lat, center.lng]}
            zoom={11}
            className="search-results-leaflet"
            style={{ width: '100%', height: '100%' }}
            scrollWheelZoom
            zoomControl={false}
          >
            <ZoomControl position="topleft" />
            <TileLayer url={MAP_TILE.url} attribution={MAP_TILE.attribution} />
            <MapResize trigger={`${hotelPoints.length}-${selectedHotelId || ''}-${searchPlace?.label || ''}`} />
            <MapSearchAreaControl searchPlace={searchPlace} onMapMoved={onMapMoved} />
            {!searchPlace && (
              <FitBounds
                hotelPoints={hotelPoints}
                fallbackCenter={mapCenter}
                totalPins={filteredPins.length}
              />
            )}
            {previewPin && (
              <PanToSelected lat={previewPin.lat} lng={previewPin.lng} active={!!selectedHotelId} />
            )}
            <HotelMapPinCluster
              pins={filteredPins}
              selectedHotelId={selectedHotelId}
              onPinClick={handlePinClick}
            />
          </MapContainer>
        </MapErrorBoundary>

        <SearchThisAreaButton
          visible={showSearchAreaBtn}
          loading={mapAreaLoading}
          onClick={onSearchThisArea}
        />

        {previewPin && (
          <div className="srm-preview-anchor">
            <MapPropertyPreviewCard
              hotel={previewPin.hotel}
              checkIn={checkIn}
              checkOut={checkOut}
              onClose={() => onClearSelection?.()}
              onViewDetails={onViewDetails}
            />
          </div>
        )}

        {geocoding && (
          <div className="search-results-map-loading" aria-live="polite">
            <span>Loading map pins…</span>
          </div>
        )}

        {!geocoding && !pins.length && (
          <div className="search-results-map-empty">
            <p>No map pins for these results.</p>
            {searchQuery && <span>Showing area: {searchQuery}</span>}
          </div>
        )}
      </div>
    </div>
  );
};

const SearchResultsGoogleMapInner = ({
  hotels,
  selectedHotelId,
  onSelectHotel,
  onViewDetails,
  onClearSelection,
  defaultCenter,
  searchQuery,
  checkIn,
  checkOut,
  viewMode,
  onViewModeChange,
  mapFullScreen,
  onToggleMapFullScreen,
  searchPlace,
  showSearchAreaBtn,
  mapAreaLoading,
  onMapMoved,
  onSearchThisArea,
  onPinsChange,
}) => {
  const mapRef = useRef(null);
  const googleMapRef = useRef(null);
  const overlaysRef = useRef([]);
  const overlayClassRef = useRef(null);
  const clusterClassRef = useRef(null);
  const zoomListenerRef = useRef(null);
  const [mapSearch, setMapSearch] = useState('');
  const [mapReady, setMapReady] = useState(false);
  const [mapError, setMapError] = useState('');

  const { pins, mapCenter, geocoding } = useHotelMapPins({
    hotels,
    searchQuery,
    checkIn,
    checkOut,
    defaultCenter,
    searchCenter: searchPlace?.center || null,
    onPinsChange,
  });

  const filteredPins = useMemo(() => {
    const q = mapSearch.trim().toLowerCase();
    if (!q) return pins;
    return pins.filter((m) => {
      const hay = `${m.name} ${m.hotel.city || ''} ${m.hotel.address || ''}`.toLowerCase();
      return hay.includes(q);
    });
  }, [pins, mapSearch]);

  const previewPin = useMemo(() => {
    if (!selectedHotelId) return null;
    return pins.find((m) => String(m.id) === String(selectedHotelId)) || null;
  }, [pins, selectedHotelId]);

  useEffect(() => {
    let cancelled = false;
    const initMap = async () => {
      try {
        const maps = await loadGoogleMaps(GOOGLE_MAPS_API_KEY);
        if (cancelled || !mapRef.current) return;
        overlayClassRef.current = createPricePinOverlayClass(maps);
        clusterClassRef.current = createClusterPinOverlayClass(maps);
        const center = mapCenter || defaultCenter;
        const map = new maps.Map(mapRef.current, {
          center: { lat: center.lat, lng: center.lng },
          zoom: 12,
          mapTypeId: 'roadmap',
          fullscreenControl: false,
          streetViewControl: false,
          mapTypeControl: false,
          zoomControl: true,
          zoomControlOptions: { position: maps.ControlPosition.TOP_LEFT },
          gestureHandling: 'greedy',
          clickableIcons: false,
        });
        googleMapRef.current = map;
        setMapReady(true);
        setMapError('');
      } catch (error) {
        if (!cancelled) setMapError(error.message || 'Google Maps failed to load');
      }
    };
    initMap();
    return () => {
      cancelled = true;
      overlaysRef.current.forEach((o) => o.setMap(null));
      overlaysRef.current = [];
    };
  }, [defaultCenter.lat, defaultCenter.lng]);

  useEffect(() => {
    const map = googleMapRef.current;
    if (!map || !mapReady || searchPlace) return;
    const center = mapCenter || defaultCenter;
    map.setCenter({ lat: center.lat, lng: center.lng });
  }, [mapCenter, mapReady, defaultCenter, searchPlace]);

  const renderGooglePins = useCallback(() => {
    const map = googleMapRef.current;
    const PricePinOverlay = overlayClassRef.current;
    const ClusterPinOverlay = clusterClassRef.current;
    if (!map || !mapReady || !PricePinOverlay || !ClusterPinOverlay) return;

    overlaysRef.current.forEach((o) => o.setMap(null));
    overlaysRef.current = [];

    const grouped = bucketPins(filteredPins, map.getZoom() || 11);
    grouped.forEach((entry) => {
      if (entry.type === 'pin') {
        const pin = entry.pin;
        const isActive = selectedHotelId && String(selectedHotelId) === String(pin.id);
        const overlay = new PricePinOverlay({
          position: new window.google.maps.LatLng(pin.lat, pin.lng),
          priceLabel: pin.priceLabel,
          isActive,
          onClick: () => {
            if (isActive) onClearSelection?.();
            else onSelectHotel?.(pin.hotel);
          },
        });
        overlay.setMap(map);
        overlaysRef.current.push(overlay);
        return;
      }

      const overlay = new ClusterPinOverlay({
        position: new window.google.maps.LatLng(entry.lat, entry.lng),
        count: entry.count,
        onClick: () => {
          map.setCenter({ lat: entry.lat, lng: entry.lng });
          map.setZoom(Math.min((map.getZoom() || 11) + 2, 16));
        },
      });
      overlay.setMap(map);
      overlaysRef.current.push(overlay);
    });
  }, [filteredPins, selectedHotelId, mapReady, onClearSelection, onSelectHotel]);

  useEffect(() => {
    renderGooglePins();
  }, [renderGooglePins]);

  const searchPlaceKeyRef = useRef(null);
  const suppressMoveRef = useRef(false);

  useEffect(() => {
    const map = googleMapRef.current;
    if (!map || !mapReady || !searchPlace?.center) return;

    const key = `${searchPlace.label}-${searchPlace.center.lat}-${searchPlace.center.lng}`;
    if (searchPlaceKeyRef.current === key) return;
    searchPlaceKeyRef.current = key;

    suppressMoveRef.current = true;
    window.setTimeout(() => {
      suppressMoveRef.current = false;
    }, 700);

    if (searchPlace.bounds) {
      const { south, west, north, east } = searchPlace.bounds;
      const bounds = new window.google.maps.LatLngBounds(
        { lat: south, lng: west },
        { lat: north, lng: east }
      );
      map.fitBounds(bounds, { top: 48, right: 48, bottom: 48, left: 48 });
      return;
    }

    map.setCenter(searchPlace.center);
    map.setZoom(searchPlace.zoom || 12);
  }, [searchPlace, mapReady]);

  useEffect(() => {
    const map = googleMapRef.current;
    if (!map || !mapReady || !onMapMoved) return undefined;

    const listener = map.addListener('idle', () => {
      if (suppressMoveRef.current) return;
      const bounds = map.getBounds();
      if (!bounds) return;
      const area = googleBoundsToArea(bounds);
      const center = map.getCenter();
      if (!center) return;
      onMapMoved({
        bounds: area,
        center: { lat: center.lat(), lng: center.lng() },
        zoom: map.getZoom(),
      });
    });

    return () => listener?.remove();
  }, [mapReady, onMapMoved]);

  useEffect(() => {
    const map = googleMapRef.current;
    if (!map || !mapReady || searchPlace) return undefined;

    if (filteredPins.length > 20) {
      const center = mapCenter || defaultCenter;
      map.setCenter({ lat: center.lat, lng: center.lng });
      map.setZoom(cityOverviewZoom(filteredPins.length));
      return undefined;
    }

    const allPoints = filteredPins.map((p) => ({ lat: p.lat, lng: p.lng }));

    if (allPoints.length === 1) {
      map.setCenter(allPoints[0]);
      map.setZoom(13);
    } else if (allPoints.length > 1) {
      const bounds = new window.google.maps.LatLngBounds();
      allPoints.forEach((p) => bounds.extend(p));
      map.fitBounds(bounds, { top: 48, right: 48, bottom: 48, left: 48 });
    }

    return undefined;
  }, [filteredPins.length, mapCenter, mapReady, defaultCenter, searchPlace]);

  useEffect(() => {
    const map = googleMapRef.current;
    if (!map || !mapReady) return undefined;

    const listener = map.addListener('zoom_changed', () => {
      window.requestAnimationFrame(renderGooglePins);
    });
    zoomListenerRef.current = listener;

    return () => {
      listener?.remove();
      zoomListenerRef.current = null;
    };
  }, [mapReady, renderGooglePins]);

  useEffect(() => {
    const map = googleMapRef.current;
    if (!map || !previewPin) return;
    map.panTo({ lat: previewPin.lat, lng: previewPin.lng });
  }, [previewPin]);

  useEffect(() => {
    const map = googleMapRef.current;
    if (!map || !mapReady) return;
    const invalidate = () => window.google?.maps?.event.trigger(map, 'resize');
    invalidate();
    const wrap = mapRef.current?.closest('.search-results-map-wrap');
    let observer;
    if (wrap && typeof ResizeObserver !== 'undefined') {
      observer = new ResizeObserver(invalidate);
      observer.observe(wrap);
    }
    window.addEventListener('resize', invalidate);
    return () => {
      observer?.disconnect();
      window.removeEventListener('resize', invalidate);
    };
  }, [mapReady, pins.length, mapFullScreen]);

  return (
    <div className={`search-results-map-panel search-results-map-panel--google ${mapFullScreen ? 'search-results-map-panel--fullscreen' : ''}`}>
      <MapFirstToolbar {...mapToolbarProps(viewMode, onViewModeChange, mapFullScreen, onToggleMapFullScreen)} />
      <div className="search-results-map-search">
        <input
          type="text"
          value={mapSearch}
          onChange={(e) => setMapSearch(e.target.value)}
          placeholder="Search on map"
          aria-label="Search on map"
        />
      </div>
      <div className="search-results-map-wrap search-results-map-wrap--google">
        <div ref={mapRef} className="search-results-google-map" />
        {mapError ? (
          <div className="search-results-map-empty">
            <p>{mapError}</p>
          </div>
        ) : null}
        {previewPin ? (
          <div className="srm-preview-anchor">
            <MapPropertyPreviewCard
              hotel={previewPin.hotel}
              checkIn={checkIn}
              checkOut={checkOut}
              onClose={() => onClearSelection?.()}
              onViewDetails={onViewDetails}
            />
          </div>
        ) : null}
        {geocoding && (
          <div className="search-results-map-loading" aria-live="polite">
            <span>Loading map pins…</span>
          </div>
        )}
        {!geocoding && !pins.length && mapReady ? (
          <div className="search-results-map-empty">
            <p>No map pins for these results.</p>
            {searchQuery ? <span>Showing area: {searchQuery}</span> : null}
          </div>
        ) : null}
        <SearchThisAreaButton
          visible={showSearchAreaBtn}
          loading={mapAreaLoading}
          onClick={onSearchThisArea}
        />
      </div>
    </div>
  );
};

const SearchResultsMap = (props) => {
  const [mapFullScreen, setMapFullScreen] = useState(false);

  const shared = {
    ...props,
    mapFullScreen,
    onToggleMapFullScreen: () => setMapFullScreen((v) => !v),
  };

  if (hasGoogleMapsKey()) return <SearchResultsGoogleMapInner {...shared} />;
  return <SearchResultsLeafletMap {...shared} />;
};

export default SearchResultsMap;
